import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CONDITION_MAP, allocateCents, getRowImage, getBuffPrice,
  isCraft, getStickerValue, getStickerPct, describeStickerPct,
} from './helpers';
import './ModalTheme.css';

/**
 * Group price — split one amount across several items already in Live.
 *
 * You buy three skins in one deal for $80. Individually the market says they
 * are worth $88 together, so the deal was struck at 90.9% of market. The only
 * cost basis that keeps every item's future profit honest is that same 90.9%
 * applied to each one: the cheap item cannot absorb the whole discount and the
 * expensive one cannot keep its full price.
 *
 * So: reference value per item → its share of the combined reference → that
 * share of the amount paid. Shares are allocated in whole cents by the
 * largest-remainder method (allocateCents), which is what makes the parts add
 * back up to the total exactly, cent for cent, no matter how the fractions fall.
 *
 * Props:
 *   items        – [{ row, realIndex }] selected on the Live tab
 *   settings     – app settings (profit/loss colours, sites)
 *   livePrices   – Buff163 price map
 *   csfloatPrices– CSFloat price map
 *   skinsData    – bundled skin database, for thumbnails
 *   inventoryMap – assetId → synced inventory item, for thumbnails
 *   variant      – 'zone' | 'classic'
 *   onConfirm    – ({ items:[{ row, realIndex, buyPrice, reference }], total, paidWith, basis }) => void
 *   onClose      – () => void
 */

// What the split is weighted by. Every one of these is a per-item value in
// dollars; the maths downstream does not care which produced it.
const BASES = [
  { key: 'buff',    label: 'Buff market' },
  { key: 'craft',   label: 'Skin + stickers' },
  { key: 'csfloat', label: 'CSFloat' },
  { key: 'buy',     label: 'Current cost' },
];

const PAID_OPTIONS = [
  { id: 'keep',    label: 'Leave as is' },
  { id: 'bank',    label: 'Cash' },
  { id: 'crypto',  label: 'Crypto' },
  { id: 'csfloat', label: 'CSFloat' },
  { id: null,      label: 'Not tracked' },
];

function csfloatPriceFor(csfloatPrices, row) {
  if (!csfloatPrices || !row?.skin?.value) return null;
  const name = row.skin.value;
  const cond = row.condition?.value ? (CONDITION_MAP[row.condition.value] || row.condition.value) : null;
  const mhn  = cond ? `${name} (${cond})` : name;
  return (csfloatPrices[mhn] || csfloatPrices[name])?.price ?? null;
}

const money = v => (v == null || Number.isNaN(v) ? 'n/a' : `$${Number(v).toFixed(2)}`);
const percent = v => (v == null || !Number.isFinite(v) ? 'n/a' : `${v.toFixed(1)}%`);

export default function GroupPriceModal({
  items, settings, livePrices, csfloatPrices, skinsData = [], inventoryMap,
  variant = 'classic', onConfirm, onClose,
}) {
  const [basis, setBasis]         = useState('buff');
  const [total, setTotal]         = useState('');
  const [paidWith, setPaidWith]   = useState('keep');
  // Hand-typed reference values, keyed by the row's index. Anything not in here
  // uses the price the chosen basis found.
  const [overrides, setOverrides] = useState({});

  // Click-outside-to-close has to mean the whole click happened outside. A click
  // event is delivered to the nearest ancestor shared by the press and the
  // release, so dragging to select the text in a price box and letting go a few
  // pixels past its edge lands on the backdrop and used to shut the modal with
  // the amount half typed.
  const pressedBackdrop = useRef(false);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Reference price each item brings to the split, before any override. Rounded
  // to cents up front so the figure in the box is exactly the one being split
  // by — a price carrying a third decimal would otherwise weight the maths
  // slightly differently from what the screen says.
  const sourced = useMemo(() => items.map(({ row, realIndex }) => {
    const cents = v => (v == null || !Number.isFinite(v) ? null : Math.round(v * 100) / 100);
    const buff = cents(getBuffPrice(livePrices, row));
    const csf  = cents(csfloatPriceFor(csfloatPrices, row));
    const buy  = cents(parseFloat(row.buyPrice)) || null;
    // A craft's bare-skin price says nothing about what it is worth in a lot:
    // two identical AKs where one carries $400 of stickers are not two equal
    // shares. Weighting by skin plus sticker value is what makes a lot of crafts
    // split sensibly.
    const craft = isCraft(row);
    const stick = craft ? getStickerValue(row, livePrices) : null;
    const withStickers = buff == null ? null : cents(buff + (stick?.total || 0));
    const auto = basis === 'buff'    ? buff
               : basis === 'craft'   ? withStickers
               : basis === 'csfloat' ? csf
               : buy;
    return { row, realIndex, buff, csf, buy, craft, stick, withStickers, auto };
  }), [items, livePrices, csfloatPrices, basis]);

  // Once a box has been touched it is the answer, emptied included: a cleared
  // box has to read as "no value yet" and block the split, not quietly fall back
  // to the market price the user just deleted.
  const refs = useMemo(() => sourced.map(s => {
    const raw = overrides[s.realIndex];
    if (raw !== undefined) {
      const n = parseFloat(raw);
      return { ...s, ref: Number.isFinite(n) && n >= 0 ? n : null, manual: true };
    }
    return { ...s, ref: s.auto, manual: false };
  }), [sourced, overrides]);

  const missing = refs.filter(r => r.ref == null || r.ref <= 0);
  const crafts  = sourced.filter(s => s.craft && s.stick?.total > 0).length;

  const totalNum   = parseFloat(total);
  const totalValid = Number.isFinite(totalNum) && totalNum >= 0;

  const split = useMemo(() => {
    if (!totalValid || missing.length > 0) return null;
    const totalCents    = Math.round(totalNum * 100);
    const combinedCents = refs.reduce((s, r) => s + Math.round(r.ref * 100), 0);
    if (combinedCents <= 0) return null;

    const cents = allocateCents(refs.map(r => Math.round(r.ref * 100)), totalCents);
    return {
      combined: combinedCents / 100,
      paidPct:  totalCents / combinedCents * 100,
      rows: refs.map((r, i) => ({
        ...r,
        assignedCents: cents[i],
        assigned:      cents[i] / 100,
        // Each item's own price as a share of its reference — all but a cent of
        // rounding away from the group percentage, and the proof of that on screen.
        ownPct:        r.ref > 0 ? (cents[i] / 100) / r.ref * 100 : null,
        was:           parseFloat(r.row.buyPrice) || 0,
      })),
    };
  }, [refs, missing.length, totalNum, totalValid]);

  const setRef = (realIndex, value) =>
    setOverrides(prev => ({ ...prev, [realIndex]: value }));

  // Switching the basis throws away hand-typed values, because they were typed
  // against the old one and silently reusing them would weight the split by a
  // mix of two sources.
  const pickBasis = key => { setBasis(key); setOverrides({}); };

  const apply = () => {
    if (!split) return;
    onConfirm?.({
      basis,
      paidWith,
      total: totalNum,
      items: split.rows.map(r => ({
        row:       r.row,
        realIndex: r.realIndex,
        buyPrice:  r.assigned.toFixed(2),
        reference: r.ref,
      })),
    });
    onClose?.();
  };

  const profC = settings?.profitTextColor || 'var(--mdl-win)';
  const lossC = settings?.lossTextColor   || 'var(--mdl-lose)';

  return (
    <div className={`mdl-overlay ${variant} top`}
      onMouseDown={e => { pressedBackdrop.current = e.target === e.currentTarget; }}
      onClick={e => { if (e.target === e.currentTarget && pressedBackdrop.current) onClose?.(); }}>
      <div className="mdl-panel" role="dialog" aria-modal="true" aria-label="Price items as a group">

        <div className="mdl-head">
          <div className="mdl-head-txt">
            <div className="mdl-title">Group price: {items.length} item{items.length !== 1 ? 's' : ''}</div>
            <div className="mdl-sub">
              Enter what you paid for the lot. Each item takes the same percentage of its own value.
            </div>
          </div>
          <button className="mdl-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="mdl-body">
          <div className="mdl-stack gap">

            {/* ── What the split is weighted by ─────────────── */}
            <div className="mdl-card">
              <div className="mdl-card-title">Value each item by</div>
              <div className="mdl-controls" style={{ marginTop: 9 }}>
                {BASES.map(b => (
                  <button key={b.key} className={`mdl-pill${basis === b.key ? ' on' : ''}`}
                    onClick={() => pickBasis(b.key)}>
                    {b.label}
                  </button>
                ))}
              </div>
              <div className="mdl-card-hint">
                Any value below can be typed over. The split follows whatever is in those boxes.
                {crafts > 0 && basis !== 'craft' && (
                  <> <strong>{crafts} of these {crafts === 1 ? 'is a craft' : 'are crafts'}</strong>, so
                  their stickers are worth more than the bare skin price this is splitting by.
                  Use <strong>Skin + stickers</strong> to weight them by the whole item.</>
                )}
              </div>
            </div>

            {/* ── The amount paid ───────────────────────────── */}
            <div className="mdl-card">
              <div className="mdl-grid2">
                <div>
                  <span className="mdl-label">Total paid for all {items.length} items (USD)</span>
                  <input type="number" className="mdl-input" min="0" step="0.01" autoFocus
                    value={total} onChange={e => setTotal(e.target.value)} placeholder="0.00" />
                </div>
                <div>
                  <span className="mdl-label">Paid with</span>
                  <div className="mdl-controls">
                    {PAID_OPTIONS.map(o => (
                      <button key={o.label} className={`mdl-pill${paidWith === o.id ? ' on' : ''}`}
                        onClick={() => setPaidWith(o.id)}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {split && (
                <div style={{ marginTop: 12 }}>
                  <div className="mdl-kv">
                    <span className="k">Combined value of the {items.length} items</span>
                    <span className="v">{money(split.combined)}</span>
                  </div>
                  <div className="mdl-kv">
                    <span className="k">You paid</span>
                    <span className="v big">{percent(split.paidPct)} of that</span>
                  </div>
                  <div className="mdl-kv">
                    <span className="k">
                      {split.paidPct <= 100 ? 'Discount on the lot' : 'Premium on the lot'}
                    </span>
                    <span className="v" style={{ color: split.paidPct <= 100 ? profC : lossC }}>
                      {money(Math.abs(split.combined - totalNum))} ({percent(Math.abs(100 - split.paidPct))})
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* ── Items with nothing to weight them by ──────── */}
            {missing.length > 0 && (
              <div className="mdl-card caution">
                <div className="mdl-card-title">
                  {missing.length} item{missing.length !== 1 ? 's have' : ' has'} no {BASES.find(b => b.key === basis)?.label.toLowerCase()} value
                </div>
                <div className="mdl-card-hint">
                  Type a value for {missing.length !== 1 ? 'them' : 'it'} below, or switch the basis. Splitting
                  without one would hand {missing.length !== 1 ? 'those items' : 'that item'} a cost of $0 and
                  overstate every future profit on {missing.length !== 1 ? 'them' : 'it'}.
                </div>
              </div>
            )}

            {/* ── Per-item table ────────────────────────────── */}
            <div className="gpm-rows">
              {refs.map((r, i) => {
                const out   = split?.rows[i];
                const img   = getRowImage(r.row, skinsData, inventoryMap);
                const bad   = r.ref == null || r.ref <= 0;
                const delta = out ? out.assigned - out.was : null;
                const sp    = out ? describeStickerPct(getStickerPct(r.row, livePrices, out.assigned)) : null;
                return (
                  <div key={r.realIndex} className={`gpm-row${bad ? ' bad' : ''}`}>
                    <div className="gpm-thumb">{img && <img src={img} alt="" />}</div>

                    <div className="gpm-id">
                      <div className="gpm-name" title={r.row.skin?.value}>{r.row.skin?.value}</div>
                      <div className="gpm-sub">
                        {r.row.condition?.value || 'no wear'}
                        {out?.was > 0 && <> · now {money(out.was)}</>}
                        {r.craft && r.stick?.total > 0 && <> · stickers {money(r.stick.total)}</>}
                      </div>
                      {/* What the share this item is about to take works out to
                          in sticker percentage, the only figure that compares
                          one craft's price to another's. */}
                      {sp && (
                        <div className="gpm-sp" title={sp.title}>{sp.label}</div>
                      )}
                    </div>

                    <div className="gpm-field">
                      <span className="mdl-label">Value</span>
                      <input type="number" className="mdl-input" min="0" step="0.01"
                        value={overrides[r.realIndex] ?? (r.auto != null ? String(r.auto.toFixed(2)) : '')}
                        onChange={e => setRef(r.realIndex, e.target.value)}
                        placeholder="0.00" />
                    </div>

                    {/* Under market is a discount and over it is an overpay, so
                        the figure is colour-coded the same way as P&L. Dead on
                        100% is neither, and stays neutral. */}
                    <div className="gpm-share" style={out && out.ownPct !== 100
                      ? { color: out.ownPct < 100 ? profC : lossC }
                      : undefined}>
                      {out
                        ? <>{percent(out.ownPct)}<span>of value</span></>
                        : <>n/a<span>of value</span></>}
                    </div>

                    <div className="gpm-out">
                      <div className="v">{out ? money(out.assigned) : 'n/a'}</div>
                      <div className="s">
                        {out
                          ? <>{percent(r.ref / split.combined * 100)} of the lot{delta ? ` · ${delta > 0 ? '+' : '−'}${Math.abs(delta).toFixed(2)}` : ''}</>
                          : 'enter a total'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>

        <div className="mdl-foot">
          <span className="mdl-foot-note">
            {split
              ? <>Assigning <strong>{money(split.rows.reduce((s, r) => s + r.assignedCents, 0) / 100)}</strong> of {money(totalNum)}. Every cent accounted for.</>
              : missing.length > 0
                ? 'Give every item a value first.'
                : 'Enter what you paid for the lot.'}
          </span>
          <button className="mdl-btn" onClick={onClose}>Cancel</button>
          <button className="mdl-btn primary" onClick={apply} disabled={!split}>
            Price {items.length} item{items.length !== 1 ? 's' : ''}
          </button>
        </div>

      </div>
    </div>
  );
}
